// Grouping and totals shared by the game reports and their PDF twins

import moment from 'moment';
import { UnpaidPayoutStatuses } from './enums';
import { getTeamDivision } from './misc';

// Games of the day split by field, in field display order
export function getGamesByField(games, day, sortedFields) {
  const dayGames = games.filter((game) => day.isSame(moment(game.date)));

  return sortedFields.map((field) => ({
    field,
    games: dayGames.filter((game) => game.field === field.id),
  }));
}

// Games of the referee split by day
export function getGamesByDay(games, referee, days) {
  const refereeGames = games.filter((game) =>
    (game.referees || []).includes(referee),
  );

  return days.map((day) => ({
    day,
    games: refereeGames.filter((game) => day.isSame(moment(game.date))),
  }));
}

// The division a game is listed and colored under: its teams', else its own
export function gameDivision(game, teamsById) {
  const team = teamsById.get(game.team1) || teamsById.get(game.team2);
  return team ? getTeamDivision(team) : game.division;
}

export const countGames = (groups) =>
  groups.reduce((total, { games }) => total + games.length, 0);

export const totalGamesOn = (name, count) =>
  `Total games on ${name}: ${count} games`;

export const totalGamesOf = (name, count) =>
  `Total games of ${name}: ${count} games`;

// A referee's pay for one tournament: `{ rate, exceptions }`, both optional —
// `rate` is missing when an exception was set before the default one, and
// `exceptions` until the first override

// What the referee is paid for one game: the exception set for it, or their
// default rate. `undefined` means nothing is set and the game is not priced yet
export const gameRate = (rates, gameId) =>
  rates?.exceptions?.[gameId] ?? rates?.rate;

export const hasRates = (rates) =>
  rates?.rate !== undefined || Object.keys(rates?.exceptions || {}).length > 0;

export const sumPay = (games, rates) =>
  games.reduce((total, game) => total + (gameRate(rates, game.id) || 0), 0);

// Always cents: it rounds away the float artefact three games at $47.5 would
// otherwise print ($142.50000000000001), and a payout sheet handed to a referee
// should not read $47.5
export const moneyStr = (amount) => `$${amount.toFixed(2)}`;

export const totalPayOn = (name, amount) =>
  `Total pay on ${name}: ${moneyStr(amount)}`;

export const totalPayOf = (name, amount) =>
  `Total pay of ${name}: ${moneyStr(amount)}`;

export const totalPayDueOf = (name, amount, paid, due) =>
  `${totalPayOf(name, amount)} (paid ${moneyStr(paid)}, due ${moneyStr(due)})`;

// Every payout but those under which no money left, in cents: summing dollars
// is what introduces the float artefact
export const sumPaid = (payouts) =>
  Math.round(
    payouts
      .filter((p) => !UnpaidPayoutStatuses.includes(p.status))
      .reduce((total, p) => total + p.amount, 0) * 100,
  ) / 100;

// Never negative: an admin may top a referee up beyond what the report priced,
// and there is nothing to offer to pay after that
export const payDue = (pay, paid) =>
  Math.max(Math.round((pay - paid) * 100) / 100, 0);
